import gameInfo from '../assets/json/gameInfo.json';
import Phaser from 'phaser';
import gameData, { GameVersions, GAME_IMAGES_JSON_PATH, GAME_IMAGES_KEY, GAME_IMAGE_BASE_PATH, SPARKLE_ANIMATION_KEY, SPARKLE_ANIMATION_PATH } from './gameData.js';
import FontKeys from './FontKeys';
import ABCyaLib from '@abcya/abcya_lib';
import ABCyaPhaser from '@abcya/abcya_phaser';
import GameStorage from './GameStorage';
import { setupAnimations } from './AnimationManager';
import TitleOddEvenScreen from './scenes/TitleOddEvenScreen';
import TitleFactorsScreen from './scenes/TitleFactorsScreen';
import TitleMultiplesScreen from './scenes/TitleMultiplesScreen';
import IntroOddEvenScreen from './scenes/IntroOddEvenScreen';
import IntroFactorsScreen from './scenes/IntroFactorsScreen';
import IntroMultiplesScreen from './scenes/IntroMultiplesScreen';
import Game from './scenes/Game';
import setupGameConfig, { gametypes } from './GameConfig';

const fonts = [];
for (const k in FontKeys) {
  fonts.push(FontKeys[k]);
}

// note: uncomment or change the following based on what you need to load
ABCyaPhaser.Preloader.PreloaderConfig.fonts = fonts;

/**
 * To load multiple image sprites, change this property to an array of Phaser.Types.Loader.FileTypes.MultiAtlasFileConfig objects.
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.imagesPath = [
 * { key: ABCyaPhaser.Images.DEFAULT_ATLAS, atlasURL: 'assets/images/' + ABCyaPhaser.Images.DEFAULT_ATLAS + '.json', path: 'assets/images' }, // default multiatlas
 * { key: "particles", atlasURL: 'assets/images/particles.json', path: 'assets/images' }
 * ]
 */
ABCyaPhaser.Preloader.PreloaderConfig.imagesPath = 'assets/images';

/**
 * To load multiple audio sprites, change this property to an array of Phaser.Types.Loader.FileTypes.AudioSpriteFileConfig objects.
 * Note: you should always include an ogg and mp3 file, the game will determine which to use based on compatibility. 
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.soundsPath = [
 * { key: ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE, 
 *   jsonURL: 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.json', 
 *   audioURL: [ 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.ogg', 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.mp3' ] }, // default sound sprite
 * { key: "extrasounds", jsonURL: 'assets/sounds/extrasounds.json', audioURL: [ 'assets/sounds/extrasounds.ogg', 'assets/sounds/extrasounds.mp3' ] }
 * ]
 */
ABCyaPhaser.Preloader.PreloaderConfig.soundsPath = 'assets/sounds';

/**
 * You can load spine objects here. This can be an object with key, jsonPath, and atlasPath properties, or an array of those objects.
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.spines = [
 * { key: "spine0", jsonPath: "assets/spines/spine0.json", atlasPath: "assets/spines/spine0.atlas" },
 * { key: "spine1", jsonPath: "assets/spines/spine1.json", atlasPath: "assets/spines/spine1.atlas" }
 * ]
 */
// ABCyaPhaser.Preloader.PreloaderConfig.spines = { key: "", jsonPath: "", atlasPath: "" };

ABCyaPhaser.Preloader.PreloaderConfig.startScene = gameData.startScene;

/**
 * Called when the preload function is done.
 * @param {Phaser.Scene} scene the preloader scene
 */
ABCyaPhaser.Preloader.PreloaderConfig.preloadCallback = (scene) => {
  // insert additional preloader functionality here; scene is the preloader scene
  scene.load.multiatlas(GAME_IMAGES_KEY, GAME_IMAGES_JSON_PATH, GAME_IMAGE_BASE_PATH);

  scene.load.spritesheet(SPARKLE_ANIMATION_KEY, SPARKLE_ANIMATION_PATH, { frameWidth: 21, frameHeight: 23 });

  scene.events.once(Phaser.Scenes.Events.CREATE, () => setupAnimations(scene));
};

(async function handshake() {
  try {
    await ABCyaLib.Environment.instance.doParentHandshake();
  }
  catch (error) {
    console.warn(error);
  }

  console.info('ABCya Game Info:', gameInfo);

  const gameMode = ABCyaLib.getConfig()?.gameMode;
  switch (gameMode) {
    case GameVersions.OddOrEven:
      gameData.scenes = [TitleOddEvenScreen, IntroOddEvenScreen];
      gameData.gameVersion = GameVersions.OddOrEven;
      gameData.MAX_NUM_VALUE = 20;
      ABCyaLib.Environment.instance.setupGameConfig(setupGameConfig);
      switch (ABCyaLib.Environment.instance.gameConfig.gametype) {
        case gametypes.even:
          gameData.PLAY_BUTTONS.push('Play');
          break;
        case gametypes.odd:
          gameData.PLAY_BUTTONS.push('Play');
          break;
        default:
          gameData.PLAY_BUTTONS.push('Odd', 'Even');
          break;
      }
      break;

    case GameVersions.Factors:
      gameData.scenes = [TitleFactorsScreen, IntroFactorsScreen];
      gameData.gameVersion = GameVersions.Factors;
      ABCyaLib.Environment.instance.setupGameConfig(setupGameConfig);
      gameData.fontColor = 0x5c4939;
      gameData.MAX_NUM_VALUE = 100;
      gameData.PLAY_BUTTONS.push('Play');
      break;

    case GameVersions.Multiples:
      gameData.scenes = [TitleMultiplesScreen, IntroMultiplesScreen];
      gameData.gameVersion = GameVersions.Multiples;
      ABCyaLib.Environment.instance.setupGameConfig(setupGameConfig);
      gameData.MAX_NUM_VALUE = 240;
      break;
  }

  gameData.scenes.push(Game);

  // setup the game storage
  gameData.gameStorage = new GameStorage(gameInfo.gameShortName + ABCyaLib.Environment.instance.gameConfig.saveKey);

  // set the best score, default back to 0 (NaN is falsy so if the number is NaN then it will fall back to 0)
  gameData.bestScore = parseInt(gameData.gameStorage.getValue('bestScore')) || 0;

  /**
     * Use the fourth argument of initializePhaserGame to override default Phaser config options
     * For example: to use the Spine plugin, the fourth argument should look like this:
     * { 
            configOverrides: {
                plugins: {
                    scene: [
                        { key: 'SpinePlugin', plugin: window.SpinePlugin, mapping: 'spine' }
                    ]
                }
            }
        }
     */
  ABCyaPhaser.Misc.initializePhaserGame(1024, 768, gameData.scenes);

  // initialize any functionality in abcya_phaser that has a dependency on abcya_lib 
  ABCyaPhaser.Misc.initializeAbcyaLibDependencies(ABCyaLib);
}());
